import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import axiosInstance from "../utlis/axiosInstance";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

interface UploadStatus {
  isUploading: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
  fileName?: string;
}

const BulkAdd: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processedFile, setProcessedFile] = useState<File | null>(null);
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    isUploading: false,
    isSuccess: false,
    isError: false,
    message: "",
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if file is CSV or XLSX
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      const isXLSX =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xlsx") ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xls");

      if (!isCSV && !isXLSX) {
        toast.error("Please select a valid CSV or XLSX file");
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size should be less than 10MB");
        return;
      }

      setSelectedFile(file);
      setProcessedFile(null); // Reset processed file on new selection
      setProcessedData([]);
      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: false,
        message: "",
      });
    }
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      // Check if file is CSV or XLSX
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
      const isXLSX =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xlsx") ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xls");

      if (!isCSV && !isXLSX) {
        toast.error("Please select a valid CSV or XLSX file");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size should be less than 10MB");
        return;
      }

      setSelectedFile(file);
      setProcessedFile(null); // Reset processed file on new selection
      setProcessedData([]);
      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: false,
        message: "",
      });
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setProcessedFile(null);
    setProcessedData([]);
    setUploadStatus({
      isUploading: false,
      isSuccess: false,
      isError: false,
      message: "",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const processFile = (file: File): Promise<{ file: File; data: any[] }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert sheet to JSON
          const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

          // Process each row
          const processedData = jsonData.map((row: any) => {
            // Handle mdn_number migration to attributes
            if (row.mdn_number) {
              let attributes = {};
              try {
                if (typeof row.attributes === "string") {
                  attributes = JSON.parse(row.attributes);
                } else if (typeof row.attributes === "object") {
                  attributes = row.attributes;
                }
              } catch (err) {
                console.warn("Failed to parse attributes for row:", row);
                attributes = {};
              }

              // Add mdn_number to attributes
              attributes = { ...attributes, mdn_number: row.mdn_number };

              // Update row
              row.attributes = JSON.stringify(attributes);

              // Remove mdn_number from root
              delete row.mdn_number;
            }
            return row;
          });

          // Convert back to worksheet
          const newWorksheet = XLSX.utils.json_to_sheet(processedData);

          // Convert to CSV
          const csvData = XLSX.utils.sheet_to_csv(newWorksheet);

          // Create new File object with BOM for Excel compatibility
          const bom = "\uFEFF";
          const newFile = new File(
            [bom + csvData],
            "processed_bulk_upload.csv",
            {
              type: "text/csv;charset=utf-8",
            },
          );

          resolve({ file: newFile, data: processedData });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsBinaryString(file);
    });
  };

  const handleDownloadProcessed = () => {
    if (!processedFile) return;

    const url = window.URL.createObjectURL(processedFile);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      processedFile.name || `processed_bulk_upload_${new Date().getTime()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please select a CSV or XLSX file first");
      return;
    }

    setUploadStatus({
      isUploading: true,
      isSuccess: false,
      isError: false,
      message: "Processing file...",
    });

    try {
      // Process the file (parse, modify, repackage as CSV)
      setUploadStatus((prev) => ({
        ...prev,
        message: "Processing data and migrating mdn_number...",
      }));
      const { file: fileToUpload, data } = await processFile(selectedFile);
      setProcessedFile(fileToUpload);
      setProcessedData(data);

      setUploadStatus((prev) => ({
        ...prev,
        message: "Uploading processed file...",
      }));

      const formData = new FormData();
      formData.append("file", fileToUpload);

      await axiosInstance.post("/gpstracker/devicehandler/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setUploadStatus({
        isUploading: false,
        isSuccess: true,
        isError: false,
        message: "File uploaded successfully!",
        fileName: selectedFile.name,
      });

      toast.success("Bulk upload completed successfully!");

      // Reset form after successful upload
      // Removed the auto-clear so user can still see and download processed file if needed
      // setTimeout(() => {
      //   handleRemoveFile();
      // }, 3000);
    } catch (error: any) {
      console.error("Upload error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Upload failed. Please try again.";

      setUploadStatus({
        isUploading: false,
        isSuccess: false,
        isError: true,
        message: errorMessage,
        fileName: selectedFile.name,
      });

      toast.error(errorMessage);
    }
  };

  const downloadSampleFile = (fileName: string) => {
    // Construct the URL for the file in the public folder
    // Adjust logic if deployed to a subdirectory
    const url = `/${fileName}`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Bulk Add Devices
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadSampleFile("sample.csv")}
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >
              Download Sample CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<TableChartIcon />}
              onClick={() => downloadSampleFile("sample.xlsx")}
              sx={{
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  borderColor: theme.palette.success.dark,
                },
              }}
            >
              Download Sample XLSX
            </Button>
          </Stack>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Upload a CSV or XLSX file to add multiple devices at once. XLSX files
          will be automatically converted to CSV format. Make sure your file
          follows the required format.
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Upload Area */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Upload CSV or XLSX File
            </Typography>

            {/* File Upload Area */}
            <Box
              sx={{
                border: `2px dashed ${selectedFile ? theme.palette.primary.main : theme.palette.grey[300]}`,
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                backgroundColor: selectedFile
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.grey[100], 0.5),
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              {!selectedFile ? (
                <Stack spacing={2} alignItems="center">
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: theme.palette.primary.main,
                    }}
                  />
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                      Drop your CSV or XLSX file here
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      or click to browse files
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      sx={{
                        bgcolor: "var(--primary-color)",
                        "&:hover": {
                          bgcolor: "var(--primary-light)",
                        },
                      }}
                    >
                      Choose File
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Stack spacing={2} alignItems="center">
                  <DescriptionIcon
                    sx={{
                      fontSize: 48,
                      color: theme.palette.success.main,
                    }}
                  />
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile();
                        }}
                        color="error"
                      >
                        Remove
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        Change File
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>

            {/* Upload Status */}
            {uploadStatus.message && (
              <Box sx={{ mt: 3 }}>
                <Alert
                  severity={
                    uploadStatus.isUploading
                      ? "info"
                      : uploadStatus.isSuccess
                        ? "success"
                        : uploadStatus.isError
                          ? "error"
                          : "info"
                  }
                  icon={
                    uploadStatus.isUploading ? (
                      <CircularProgress size={20} />
                    ) : uploadStatus.isSuccess ? (
                      <CheckCircleIcon />
                    ) : uploadStatus.isError ? (
                      <ErrorIcon />
                    ) : undefined
                  }
                >
                  {uploadStatus.message}
                </Alert>
              </Box>
            )}

            {/* Submit Button */}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default BulkAdd;
